import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setCredentials, logOut, sessionExpired } from "../../features/auth/authSlice";
import errorLogger from '../../services/frontendErrorLogger';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState, endpoint, type, body }) => {
    // Only use token from Redux state
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    // Only set Content-Type for non-FormData requests
    if (
      body &&
      !(body instanceof FormData) &&
      !headers.has('Content-Type')
    ) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  try {
    //console.log('Making request to:', args.url);
    
    let result = await baseQuery(args, api, extraOptions);
    
    // Handle API errors (status >= 400)
    if (result?.error) {
      const errorStatus = result.error.status;
      const errorMessage = result.error?.data?.message || 'Unknown API error';
      
      // For server errors (500+), log to our error service
      if (errorStatus >= 500) {
        errorLogger.logError(
          new Error(`API ${errorStatus} error: ${errorMessage}`),
          'API Request',
          {
            endpoint: args.url,
            method: args.method,
            status: errorStatus,
            response: result.error?.data
          }
        );
      }
      
      // Only do token refresh for auth errors
      if (errorStatus === 401 || errorStatus === 403) {
        //console.log('Token expired or invalid, attempting refresh...');

        // Try to get a new token
        const refreshResult = await baseQuery("/auth/refresh", api, extraOptions);

        if (refreshResult?.data) {
          //console.log('Token refresh successful');

          // Store the new token
          api.dispatch(setCredentials({ accessToken: refreshResult.data.accessToken }));

          // Retry the original query with new access token
          result = await baseQuery(args, api, extraOptions);
        } else {          
          // If all recovery attempts failed, mark the session as expired
          api.dispatch(sessionExpired());

          if (refreshResult?.error?.status === 403) {
            refreshResult.error.data.message = "Your login has expired.";
          }
          return refreshResult;
        }
      }
    }

    return result;
    
  } catch (error) {
    // Catch any unexpected errors in the baseQuery itself
    errorLogger.logError(
      error,
      'API Client',
      {
        endpoint: args?.url,
        method: args?.method
      }
    );
    
    // Return a formatted error that matches RTK Query's expected format
    return {
      error: {
        status: 'FETCH_ERROR',
        error: error.message,
        data: { message: 'An unexpected error occurred while processing your request' }
      }
    };
  }
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Bid",
    "User",
    "BidOffer",
    "BidRequest",
    "Company",
    "Document",
    "Chat",
    "Term",
    "SingleBid",
    "System",
    "Category",
    "Product",
    "Participant",
    "Procurement",
    "Offers",
    "Template",
    "ActivityLog",
    "ActivityLogSummary",
    "ErrorLogs",
    "ErrorLogsSummary"
  ],
  endpoints: (builder) => ({}),
});